import { useMemo } from 'react';
import { cleanText } from '../components/utils';
import { useGetAllTopicsQuery } from '../slices/topicsApiSlice';

export const useSearchResults = (query) => {
  const { data: topics, isLoading, isSuccess, isError, error } = useGetAllTopicsQuery();

  return useMemo(() => {
    if (query.length === 0 || !isSuccess) return [];

    const lowerSearch = cleanText(query).toLowerCase();
    
    // Trouver l'article suggéré et ses tags associés
    const suggestArticle = topics.find(
      article => lowerSearch === article.suggestion?.toLowerCase()
    );
    const tagsMatches = suggestArticle ? suggestArticle.tags : [cleanText(query)];

    // Calculer le score et filtrer les articles
    const scoredResults = topics
      .map(article => {
        let score = 0;
        const matchDetails = [];

        // Score pour le titre
        const titleMatches = (cleanText(article.title)?.toLowerCase().match(new RegExp(lowerSearch, 'g')) || []).length;
        if (titleMatches > 0) {
          score += titleMatches * 4;
          matchDetails.push(`title(${titleMatches})`);
        }

        // Score pour la description
        const descriptionMatches = (cleanText(article.description)?.toLowerCase().match(new RegExp(lowerSearch, 'g')) || []).length;
        if (descriptionMatches > 0) {
          score += descriptionMatches * 2;
          matchDetails.push(`desc(${descriptionMatches})`);
        }

        // Score pour les tags
        const matchingTags = article.tags?.filter(tag => 
          tagsMatches.some(match => cleanText(tag).includes(cleanText(match)))
        ) || [];
        if (matchingTags.length > 0) {
          score += matchingTags.length * 5;
          matchDetails.push(`tags(${matchingTags.length})`);
        }

        // Score amélioré pour les sous-titres
        const subtitles = article.subtitles?.filter(subtitle => {
          const normalizedSubtitle = cleanText(subtitle).toLowerCase();
          
          // Vérification exacte
          if (normalizedSubtitle.includes(lowerSearch)) {
            return true;
          }

          // Vérification mot par mot
          const searchWords = lowerSearch.split(/\s+/);
          return searchWords.every(word => normalizedSubtitle.includes(word));
        }) || [];
        if (subtitles.length > 0) {
          score += subtitles.length * 3;
          matchDetails.push(`subtitles(${subtitles.length})`);
        }

        return {
          ...article,
          score,
          matchDetails: matchDetails.join(', ')
        };
      })
      .filter(article => article.score > 0)
      .sort((a, b) => b.score - a.score);

    return scoredResults;
  }, [query, isSuccess, topics]);
};